import "@danishagro/shared/src/styles/global/main.scss";
import "react-loading-skeleton/dist/skeleton.css";
import React, { useEffect, useRef, useState, lazy, Suspense } from "react";
import { AppDataProvider, useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import { BasketProvider } from "@danishagro/shared/src/contexts/basket/basket.context";
import { DA_SimpleHeader } from "@danishagro/shared/src/components/organisms/SimpleHeader/SimpleHeader.component";
import { DA_SiteFooter } from "@danishagro/shared/src/components/organisms/SiteFooter/SiteFooter.component";
import { B2C_SiteHeader } from "@organisms/SiteHeader/SiteHeader.component";
import { GoogleTagManager } from "@danishagro/shared/src/components/atoms/GoogleTagManager/GoogleTagManager.component";
import { Helmet } from "react-helmet";
import { ModalProvider, ModalSize, useModal } from "@danishagro/shared/src/contexts/modal.context";
import { PageProvider, usePage } from "@danishagro/shared/src/contexts/page.context";
import { SiteHeaderProvider } from "@danishagro/shared/src/contexts/siteHeader.context";
import { Toaster } from "react-hot-toast";
import { ScreenProvider, useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { TranslationsProvider } from "@danishagro/shared/src/contexts/translations/translations.context";
import { BottomSheetProvider } from "@danishagro/shared/src/contexts/bottomSheet/bottomSheet.context";
import { NotificationsProvider } from "@danishagro/shared/src/components/organisms/SiteHeader/hooks/useNotifications.hook";
import { DA_ShopPicker } from "@organisms/ShopPicker/ShopPicker.component";
import { getUrl } from "@danishagro/shared/src/helpers/getUrl.helper";
import { isDevMode } from "@danishagro/shared/src/helpers/isDevMode.helper";
import { getGlobalVariables } from "@helpers/getGlobalVariables.helper";
import { DA_ErrorBoundary } from "@danishagro/shared/src/components/molecules/ErrorBoundary/ErrorBoundary.component";
import { DA_PageNotFound } from "@danishagro/shared/src/components/organisms/PageNotFound/PageNotFound.component";
import useTrackPageView from "@danishagro/shared/src/gtm/useTagManger";
import useTrackScrollDistance from "@danishagro/shared/src/gtm/useTrackScrollDistance ";
import {
    RelewiseProvider,
    priceSortingOptions,
} from "@danishagro/shared/src/contexts/relewise.context";
import { FavoriteProvider } from "@contexts/favorites.context";
import { B2C_SiteHeaderB2B } from "@organisms/SiteHeaderB2B/SiteHeader.component";
import { ScreenReaderProvider } from "@danishagro/shared/src/contexts/useScreenReader.context";
import { PageLocationProvider } from "@contexts/PageLocationContext";
import { PreviewPageLocationProvider } from "@contexts/PreviewPageLocationContext";
import { DA_PrivacyModal } from "@organisms/PrivacyModal/PrivacyModal.component";
import DA_HomePage from "./pages/Home.page";
import DA_ReceiptPage from "./pages/Receipt.page";
import DA_PreviewPage from "./pages/Preview.page";

// Lazy imports with chunk names:
const DA_PlaceholderPage = lazy(
    () =>
        import(
            /* webpackChunkName: "PlaceholderPageChunk" */
            "./pages/Placeholder.page"
        )
);

const DA_ResultsPage = lazy(
    () =>
        import(
            /* webpackChunkName: "ResultsPageChunk" */
            "./pages/Results.page"
        )
);

const GlobalLogic = ({ children }: { children: React.ReactNode }): JSX.Element => {
    const { isScrollLocked, scrollTo } = useScreen();
    const { isModalShowing, showModal } = useModal();
    const { pageIsLoading } = usePage();
    const { acceptedPolicy } = useAppData();

    const [shouldShowModal, setShouldShowModal] = useState<boolean>(false);

    useEffect(() => {
        if (acceptedPolicy === "UNDECIDED" && globalThis.siteVariant === "hatting-agro-b2b") {
            showModal(<DA_PrivacyModal />, {
                shouldCloseOnOverlayClick: false,
                shouldCloseOnEsc: false,
            });
        }
    }, [acceptedPolicy, showModal]);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const isCampaign = searchParams.get("campaign") === "true";

        // Check if it's a campaign URL immediately
        if (globalThis.disableB2BShopPickerPopup) {
            //Don't show modal
        } else if (isCampaign) {
            localStorage.setItem(`b2bShop`, `accepted`);
        } else {
            // Only show the modal if not a campaign and other conditions are met
            if (
                !globalThis.isAuthenticated &&
                localStorage.getItem(`b2bShop`) !== `accepted` &&
                getUrl("b2cShop")
            ) {
                setShouldShowModal(true);
            }
        }
    }, []);

    useEffect(() => {
        if (shouldShowModal) {
            showModal(<DA_ShopPicker />, {
                shouldCloseOnOverlayClick: false,
                shouldCloseOnEsc: false,
                size: ModalSize.SM,
            });
        }
    }, [showModal, shouldShowModal]);

    useEffect(() => {
        if (isModalShowing || isScrollLocked) {
            // Enable scroll lock
            document.documentElement.classList.add("scroll-locked");
        } else {
            // Disable scroll lock
            document.documentElement.classList.remove("scroll-locked");
        }
    }, [isModalShowing, isScrollLocked]);

    useEffect(() => {
        if (pageIsLoading) {
            document.body.classList.add("page-is-loading");
        } else {
            document.body.classList.remove("page-is-loading");

            // Only apply scroll restoration logic for danish-agro-b2c site variant
            if (globalThis?.siteVariant === "danish-agro-b2c") {
                // Check if this is a ProductGroup (category) page that should handle its own scroll position
                const isProductGroupPage = globalThis?.pageContent?.pageAlias === "ProductGroup";

                // Check if there's a saved scroll position for this page (indicating back navigation)
                const savedScrollData = sessionStorage.getItem("categoryScrollPosition");
                const shouldRestoreScroll = isProductGroupPage && savedScrollData;

                if (shouldRestoreScroll) {
                    // Let the category component handle scroll restoration
                } else {
                    scrollTo(0, true);
                }
            } else {
                // For all other site variants, always scroll to top
                scrollTo(0, true);
            }
        }
    }, [pageIsLoading, scrollTo]);

    return (
        <>
            {globalThis.googleTagManagerId && (
                <GoogleTagManager gtmKey={globalThis.googleTagManagerId} />
            )}

            <Toaster
                position="top-right"
                toastOptions={{
                    style: {
                        backgroundColor: "var(--color-grey-90)",
                    },
                    custom: {
                        duration: 1500,
                    },
                }}
                containerClassName="toaster"
            />
            {children}
        </>
    );
};

const Contexts = ({ children }: { children: React.ReactNode }): JSX.Element => {
    const { isAppReady } = useAppData();

    const LocationProvider = globalThis.isPreview
        ? PreviewPageLocationProvider
        : PageLocationProvider;

    return isAppReady ? (
        <ScreenReaderProvider>
            <BasketProvider>
                <NotificationsProvider>
                    <PageProvider>
                        <ScreenProvider>
                            <ModalProvider>
                                <RelewiseProvider
                                    optionalSorting={priceSortingOptions}
                                    paginationPageSize={24}
                                >
                                    <LocationProvider>
                                        <BottomSheetProvider>
                                            <FavoriteProvider>
                                                <GlobalLogic>{children}</GlobalLogic>
                                            </FavoriteProvider>
                                        </BottomSheetProvider>
                                    </LocationProvider>
                                </RelewiseProvider>
                            </ModalProvider>
                        </ScreenProvider>
                    </PageProvider>
                </NotificationsProvider>
            </BasketProvider>
        </ScreenReaderProvider>
    ) : null;
};

export const ContextWrapper = ({ children }: { children: React.ReactNode }): JSX.Element => {
    const siteVariant: string = globalThis.siteVariant;

    return (
        <AppDataProvider
            currentSite="B2C"
            getGlobalVariables={(customerNumber, forceNew) =>
                getGlobalVariables(customerNumber, forceNew, siteVariant)
            }
        >
            <TranslationsProvider>
                <DA_ErrorBoundary
                    featureName="B2C"
                    featureVersion="v0"
                    errorComponent={
                        <div
                            style={{
                                background: "var(--color-grey-5)",
                                boxShadow: "var(--shadow-level-1)",
                            }}
                        >
                            <DA_PageNotFound code={500} />
                        </div>
                    }
                >
                    <Contexts>{children}</Contexts>
                </DA_ErrorBoundary>
            </TranslationsProvider>
        </AppDataProvider>
    );
};

const RoutesComponent = () => {
    const { pageTree } = usePage();
    const { pathname } = useLocation();

    // GTM Tracking
    // TODO: Add pageContent.alias to B2B
    useTrackPageView(globalThis?.pageContent?.pageAlias);
    useTrackScrollDistance("scroll_distance");

    const customerServiceLinks = useRef(
        globalThis.footerObj?.columns?.[0]?.items?.filter(({ itemType }) => itemType === "link") ||
            []
    ).current;

    const isPreview = globalThis.isPreview;
    const isCheckoutPage = /^\/checkout(\/.*)?$/.test(pathname);

    const renderHeader = () => {
        // First check if we're on checkout page
        if (isCheckoutPage) {
            return <DA_SimpleHeader />;
        }

        // If not on checkout page, determine which header type to use
        return globalThis.siteSettingsObj.siteHeader === "hatting-agro" ? (
            <B2C_SiteHeaderB2B customerServiceLinks={customerServiceLinks} />
        ) : (
            <B2C_SiteHeader customerServiceLinks={customerServiceLinks} />
        );
    };

    return (
        <>
            <Helmet>
                <meta
                    name="format-detection"
                    content="telephone=no,date=no,address=no,email=no,url=no"
                />
            </Helmet>
            <div className="page-container b2c">
                <SiteHeaderProvider
                    pageTree={pageTree}
                    showServiceMenu={globalThis.apiGatewayUrl || isDevMode}
                >
                    {renderHeader()}
                </SiteHeaderProvider>

                <main id="main">
                    <Suspense fallback={<div className="page-container" />}>
                        <Routes>
                            {/* If in preview mode, render directly from globalThis.pageContent without any API fetch */}
                            {isPreview ? (
                                <Route path="*" element={<DA_PreviewPage />} />
                            ) : (
                                <>
                                    <Route path="/" element={<DA_HomePage />} />

                                    <Route
                                        path="/receipt/:orderId/:orderSecret"
                                        element={<DA_ReceiptPage />}
                                    />

                                    <Route path="*" element={<DA_PlaceholderPage />} />

                                    <Route path="/searchresults" element={<DA_ResultsPage />} />
                                </>
                            )}
                        </Routes>
                    </Suspense>
                </main>

                <DA_SiteFooter
                    data={
                        isCheckoutPage
                            ? globalThis?.siteSettingsObj?.miniFooter
                            : globalThis?.footerObj
                    }
                    isMiniFooter={isCheckoutPage}
                />
            </div>
        </>
    );
};

export const App = () => (
    <Router>
        <ContextWrapper>
            <RoutesComponent />
        </ContextWrapper>
    </Router>
);
